import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import Services from './pages/Services';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import Careers from './pages/Careers'; 

// Particle Engine for AI Vibe
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";

function App() {
  // 1. Define all valid pages
  const validPages = ['home', 'services', 'pricing', 'contact', 'careers'];

  // 2. Initialize state by reading the URL hash (Fixes the Refresh Issue)
  const getInitialPage = () => {
    const hash = window.location.hash.replace('#', '');
    return validPages.includes(hash) ? hash : 'home';
  };

  const [activePage, setActivePage] = useState(getInitialPage);
  const [init, setInit] = useState(false);

  // Initialize the neural-link particles on mount
  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => setInit(true));
  }, []);

  // 3. Sync State to URL Hash (Adds to Browser History for the Back Button)
  useEffect(() => {
    if (window.location.hash !== `#${activePage}`) {
      window.location.hash = activePage;
    }
  }, [activePage]);

  // 4. Listen for Browser Back/Forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (validPages.includes(hash)) {
        setActivePage(hash);
      } else {
        setActivePage('home');
      }
    };

    // Attach event listener for when the user presses the hardware back button
    window.addEventListener('hashchange', handleHashChange);
    
    // Cleanup listener on unmount
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Page Router
  const renderPage = () => {
    switch (activePage) {
      case 'home': 
        return <Home setPage={setActivePage} />;
      case 'services': 
        return <Services />;
      case 'pricing': 
        return <Pricing />;
      case 'contact': 
        return <Contact />;
      case 'careers':
        return <Careers />;
      default: 
        return <Home setPage={setActivePage} />;
    }
  };

  // Particle configuration (Gold Brand Theme)
  const particlesOptions = {
    background: { color: { value: "transparent" } },
    fpsLimit: 120,
    particles: {
      color: { value: "#ffb700" },
      links: { 
        color: "#ffb700", 
        distance: 150, 
        enable: true, 
        opacity: 0.1, 
        width: 1 
      },
      move: { 
        enable: true, 
        speed: 0.6, 
        direction: "none", 
        outModes: { default: "bounce" } 
      },
      number: { 
        density: { enable: true, area: 800 }, 
        value: 45 
      },
      opacity: { value: 0.2 },
      shape: { type: "circle" },
      size: { value: { min: 1, max: 2 } },
    },
  };

  return (
    <div className="bg-[#020408] min-h-screen text-white selection:bg-[#ffb700] selection:text-black font-sans flex flex-col">
      
      {/* Background Neural Network Layer */}
      {init && (
        <Particles 
          id="tsparticles" 
          options={particlesOptions} 
          className="absolute inset-0 z-0 pointer-events-none" 
        />
      )}

      {/* Global Navbar */}
      <Navbar setPage={setActivePage} activePage={activePage} />

      {/* Page Content with Framer Motion Transitions */}
      <main className="relative z-10 flex-grow">
        <AnimatePresence mode="wait">
          <motion.div
            key={activePage}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            {renderPage()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer - Now visible on ALL pages */}
      <div className="relative z-10">
        <Footer />
      </div>
      
    </div>
  );
}

export default App;